<template>
  <v-parallax class="hero" src="/img/hero-room.webp" alt="Jasmine Therapies treatment room">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You are one step away from</div>
        <div class="text-center text-h2 pa-2">stronger, faster recovery</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.SportsMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our sports massage?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.SportsMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by our clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text" v-html="review.quote"></p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.SportsMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Sports Massage?"
    :benefits="benefits"
    :booking-url="$bookingUrls.SportsMassage.Default" />

  <section id="pricing">
    <div class="full-width-header">Individual Sessions & Packages</div>
    <div class="content pb-16">
      <p class="content-intro">
        Choose between our flexible individual sessions or value-packed 5-session bundles designed to keep you moving
        at your best.
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="5-Session Packages"
          :booking-options="packageOptions"
          :link-target="'_self'"
          class="service-item-card" />

        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel stronger and looser?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Recover faster, move better, and stay on top of your training. Book your sports massage today.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.SportsMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'SportsMassageView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Performance-focused relief',
          description: 'Targeted work for tight, overworked muscles so you can train and move comfortably.'
        },
        {
          title: 'Faster recovery',
          description: 'Helps reduce muscle soreness and supports circulation after intense activity.'
        },
        {
          title: 'Injury-prevention support',
          description: 'We assess movement patterns and release areas that commonly lead to strain.'
        }
      ],
      starsImage: '/img/five-stars.png',
      featureReviews: [
        {
          name: 'Alex Bantzos',
          quote:
            "I've had 3 sports massage sessions with Jasmine and she literally found knots and strained muscles I didn't even know it's possible.<br><br>She could even find knots on muscles just by liking at them!<br><br>Will definitely visit again soon!!"
        },
        {
          name: 'Dave Whelan',
          quote:
            'Really great sports massage. I had a terrible pain in my lower back from doing deadlifts in the gym. Jasmine did a great job and did a very intense massage. She also helped with knots in my lower leg. I can walk normally now just after two days without any pain. Totally recommend. Great stuff.'
        }
      ],
      benefits: [
        {
          title: 'Enhances Performance',
          description: 'Prepares muscles for peak output and helps maintain optimal condition between training sessions.'
        },
        {
          title: 'Prevents Injuries',
          description: 'Identifies and addresses muscle imbalances, tightness, and weak points before they become problems.'
        },
        {
          title: 'Accelerates Recovery',
          description: 'Reduces post-exercise soreness and speeds up muscle repair, so you can get back to training sooner.'
        },
        {
          title: 'Improves Accuracy & Control',
          description: 'Releases tension in key muscle groups to improve range of motion and fine motor control — essential for precision sports like golf, tennis, and cricket.'
        }
      ],
      packageOptions: [
        new BookingOption('5 x 50 min', BookingUrls.SportsMassage.Package50),
        new BookingOption('5 x 1h 15min', BookingUrls.SportsMassage.Package75)
      ],
      sessionOptions: [
        new BookingOption('50 min', BookingUrls.SportsMassage.Min50),
        new BookingOption('1h 15min', BookingUrls.SportsMassage.Min75)
      ]
    };
  }
});
</script>
